// src/pages/frontend/CheckoutPage.tsx
import {
  Check,
  ChevronRight,
  ShoppingCart as ShoppingCartIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { Box, Button, Container, Paper, Typography } from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  UNSAFE_NavigationContext as NavigationContext,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  CheckoutItem,
  CheckoutSidebar,
  CheckoutSidebarActions,
  DeliveryInfo,
  DeliveryInformation,
  OrderSummary,
  PaymentSection,
} from "../../components/frontend/checkout";
import { SHIPPING } from "../../config/constants";
import type { PaymentCtaState } from "../../components/frontend/checkout/PaymentSection";
import { PAYMENT_CTA_DEFAULT_STATE } from "../../components/frontend/checkout/PaymentSection";
import { amazonColors } from "../../config/amazonTheme";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { usePublishTopOffset } from "../../hooks/usePublishTopOffset";
import { PaymentMethodDiscountValidationResult } from "../../services/paymentMethodDiscountService";
import { getActiveTaxAndFeeSettings } from "../../services/taxAndFeeService";
import { useAuthStore } from "../../store/useAuthStore";
import { useCartStore } from "../../store/useCartStore";
import { TaxAndFeeSetting } from "../../types/admin";
import { PaymentError, PaymentSuccess } from "../../types/payment";
import {
  calculateServiceFee,
  calculateTax,
  roundCurrency,
} from "../../utils/checkoutTotals";
import { trackPurchase } from "../../utils/metaPixel";
import { extractEstimatedWeightInKg } from "../../utils/productUtils";

type StepId = "summary" | "delivery" | "payment";

interface StepConfig {
  id: StepId;
  label: string;
}

interface BuyNowItemPayload {
  id: string;
  title: string;
  price: number;
  quantity: number;
  image?: string;
  shippingCost?: number;
  retailer?: string;
  shippingCalculated?: boolean;
  offerShippingFee?: number;
  sellerName?: string;
  sellerId?: string;
  offerId?: string;
}

const STEPS_ORDER: StepId[] = ["summary", "delivery", "payment"];

const CheckoutPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { navigator: routerNavigator } = useContext(NavigationContext);
  const { user } = useAuthStore();
  const {
    items,
    isCalculatingShipping,
    hasItemsNeedingShipping,
    calculateMissingShipping,
    removeSelectedItems,
    getItemsNeedingShipping,
  } = useCartStore();

  // publish top offset so we can pin this page under the header/breadcrumbs
  usePublishTopOffset();
  const { isMobile } = useBreakpoint();

  const [activeStep, setActiveStep] = useState(0);
  const [paymentCtaState, setPaymentCtaState] = useState<PaymentCtaState>(
    PAYMENT_CTA_DEFAULT_STATE
  );
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [, setOrderNumber] = useState<string | null>(null); // Will be set from backend response
  const [paymentMethodDiscount, setPaymentMethodDiscount] =
    useState<PaymentMethodDiscountValidationResult | null>(null);
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo>({
    addressId: undefined,
    fullName: user ? `${user.firstName} ${user.lastName}` : "",
    email: user?.email || "",
    address: "",
    addressLine2: "",
    city: "",
    region: "",
    zipCode: "",
    country: "Tajikistan",
    phone: "",
    deliveryInstructions: "",
    pickupLocation: "Dushanbe",
  });

  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [priceUpdateData, setPriceUpdateData] = useState<{
    updatedTotalPrice?: number;
    updatedShippingCost?: number;
    updatedTax?: number;
    updatedFees?: number;
    updatedFinalTotal?: number;
  } | null>(null);
  const [taxAndFeeSettings, setTaxAndFeeSettings] = useState<
    TaxAndFeeSetting[]
  >([]);
  const [appliedPromoCode, setAppliedPromoCode] = useState<{
    code: string | null;
    codeId: string | null;
    discountAmount: number;
  }>({
    code: null,
    codeId: null,
    discountAmount: 0,
  });
  const buyNowItem = (
    location.state as { buyNowItem?: BuyNowItemPayload } | null
  )?.buyNowItem;
  const isBuyNowFlow = Boolean(buyNowItem);
  const [buyNowShippingCost, setBuyNowShippingCost] = useState<number | null>(
    buyNowItem?.shippingCost ?? null
  );
  const [isBuyNowCalculatingShipping, setIsBuyNowCalculatingShipping] =
    useState(false);
  const allowPaymentNavigationRef = useRef(false);

  // Fetch active tax and fee settings on component mount, retry once on failure
  useEffect(() => {
    let cancelled = false;

    const fetchTaxAndFeeSettings = async (attempt = 1) => {
      try {
        const response = await getActiveTaxAndFeeSettings();
        if (cancelled) return;
        if (response.isSuccess && response.data && response.data.length > 0) {
          setTaxAndFeeSettings(response.data);
        } else if (attempt === 1) {
          // Retry once — transient API errors can return isSuccess: false
          setTimeout(() => fetchTaxAndFeeSettings(2), 2000);
        } else {
          console.warn("Tax and fee settings unavailable; service fee will be 0.");
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to fetch tax and fee settings:", error);
        if (attempt === 1) {
          setTimeout(() => fetchTaxAndFeeSettings(2), 2000);
        }
      }
    };

    fetchTaxAndFeeSettings();
    return () => { cancelled = true; };
  }, []);

  // Calculate shipping ONCE when page loads (if needed)
  useEffect(() => {
    if (isBuyNowFlow) return;

    const itemsNeedingShipping = getItemsNeedingShipping();
    if (itemsNeedingShipping.length > 0 && !isCalculatingShipping) {
      calculateMissingShipping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - run ONCE on mount

  // Calculate shipping for buy-now items when delivery info is available
  useEffect(() => {
    if (!isBuyNowFlow || !buyNowItem) return;
    if (
      buyNowItem.shippingCalculated ||
      buyNowItem.shippingCost !== undefined ||
      buyNowShippingCost !== null
    ) {
      return; // Already calculated
    }

    // Only calculate if we have delivery address
    if (!deliveryInfo.addressId) return;

    const calculateBuyNowShipping = async () => {
      setIsBuyNowCalculatingShipping(true);
      try {
        const { shippingService } = await import(
          "../../services/shippingService"
        );
        // For buy-now items, we may not have full product details
        // So we only pass productId, retailer, and title
        const result = await shippingService.calculateShipping({
          productId: buyNowItem.id,
          retailer: buyNowItem.retailer || "amazon",
          productTitle: buyNowItem.title,
        });
        if (result.shippingCost !== undefined) {
          setBuyNowShippingCost(result.shippingCost);
        }
      } catch (error) {
        // Don't set default shipping cost on error - leave it as null
        // The shipping will remain uncalculated
      } finally {
        setIsBuyNowCalculatingShipping(false);
      }
    };

    calculateBuyNowShipping();
  }, [
    isBuyNowFlow,
    buyNowItem?.id,
    buyNowItem?.retailer,
    buyNowItem?.shippingCalculated,
    buyNowItem?.shippingCost,
    buyNowShippingCost,
    deliveryInfo.addressId,
  ]);

  // Scroll to top when step changes (for mobile view)
  useEffect(() => {
    const behavior = isMobile ? "auto" : "smooth";
    window.scrollTo({ top: 0, left: 0, behavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [activeStep, isMobile]);

  const isPaymentProcessing = paymentCtaState.key === "processing";

  useEffect(() => {
    if (!isPaymentProcessing) return;

    const navigatorWithBlock = routerNavigator as {
      block?: (blocker: (tx: { retry: () => void }) => void) => () => void;
    };

    if (!navigatorWithBlock.block) return;

    const unblock = navigatorWithBlock.block((tx: { retry: () => void }) => {
      if (!allowPaymentNavigationRef.current) {
        return;
      }
      allowPaymentNavigationRef.current = false;
      unblock();
      tx.retry();
    });

    return unblock;
  }, [isPaymentProcessing, routerNavigator]);

  const handleDeliveryInfoChange = useCallback(
    (field: keyof DeliveryInfo, value: string) => {
      setDeliveryInfo((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const stepsConfig: StepConfig[] = useMemo(
    () =>
      STEPS_ORDER.map((id) => ({
        id,
        label:
          id === "summary"
            ? t("checkout.steps.orderSummary")
            : id === "delivery"
              ? t("checkout.steps.deliveryInfo")
              : t("checkout.steps.payment"),
      })),
    [t]
  );

  const goToStep = useCallback((index: number) => {
    const safeIndex = Math.max(0, Math.min(index, STEPS_ORDER.length - 1));
    setActiveStep(safeIndex);
  }, []);

  const currentStepId = stepsConfig[activeStep]?.id ?? "summary";

  // Build checkout items from either buy-now payload or cart
  const checkoutItems: CheckoutItem[] = useMemo(() => {
    if (buyNowItem) {
      // Use calculated shipping cost if available, otherwise use from payload
      const shippingCost =
        buyNowShippingCost !== null
          ? buyNowShippingCost
          : (buyNowItem.shippingCost ?? 0);
      const shippingCalculated =
        buyNowShippingCost !== null ||
        buyNowItem.shippingCalculated ||
        buyNowItem.shippingCost !== undefined;

      // For buy-now items, weight extraction is not available since we don't have full product data
      // This is acceptable - the backend can calculate it if needed
      return [
        {
          id: buyNowItem.id,
          productId: buyNowItem.id,
          title: buyNowItem.title,
          price: Number(buyNowItem.price) || 0,
          quantity: buyNowItem.quantity,
          image: buyNowItem.image || "/placeholder-product.png",
          shippingCost,
          shippingCalculated,
          selected: true,
          retailer: buyNowItem.retailer || "Unknown",
          cargoPrice: shippingCost,
          estimatedWeight: undefined, // Not available for buy-now items without full product data
          sellerName: buyNowItem.sellerName,
          sellerId: buyNowItem.sellerId,
          offerId: buyNowItem.offerId,
          sellerShippingFee: buyNowItem.offerShippingFee,
        },
      ];
    }

    return items.map((item) => {
      // Extract estimated weight from product data
      const estimatedWeight = extractEstimatedWeightInKg(item.product);

      return {
        id: item.cartItemId,
        productId: item.product.product_id,
        title: item.product.title,
        price: Number(item.product.price) || 0,
        quantity: item.quantity,
        image:
          item.product.image ||
          item.product.main_image ||
          "/placeholder-product.png",
        shippingCost: Number(item.shippingCost) || 0,
        shippingCalculated: item.shippingCalculated,
        selected: selectedItems.has(item.cartItemId),
        retailer: item.product.retailer || "Unknown",
        // Use shipping cost as cargo price (already calculated for each item)
        cargoPrice: Number(item.shippingCost) || 0,
        estimatedWeight,
        sellerName: item.sellerName,
        sellerId: item.sellerId,
        offerId: item.offerId,
        sellerShippingFee: item.offerShippingFee,
      };
    });
  }, [buyNowItem, buyNowShippingCost, items, selectedItems]);

  // Calculate totals based on selected items
  const selectedItemsList = isBuyNowFlow
    ? checkoutItems
    : checkoutItems.filter((item) => item.selected === true);
  const selectedItemsNeedingShipping = useMemo(() => {
    if (isBuyNowFlow) {
      // For buy-now, check if shipping is being calculated or not calculated yet
      return isBuyNowCalculatingShipping ||
        (buyNowItem &&
          !buyNowItem.shippingCalculated &&
          buyNowShippingCost === null)
        ? [buyNowItem!] // Return a dummy array to indicate shipping is needed
        : [];
    }
    return items.filter(
      (item) =>
        selectedItems.has(item.cartItemId) &&
        item.shippingCalculated !== true
    );
  }, [
    isBuyNowFlow,
    isBuyNowCalculatingShipping,
    buyNowItem,
    buyNowShippingCost,
    items,
    selectedItems,
  ]);
  const hasSelectedItemsNeedingShipping =
    selectedItemsNeedingShipping.length > 0;

  // Use price update data if available, otherwise calculate from items
  const selectedItemsCount = selectedItemsList.reduce(
    (sum, item) => sum + item.quantity,
    0
  );
  const selectedTotalAmount =
    priceUpdateData?.updatedTotalPrice ??
    selectedItemsList.reduce(
      (sum, item) => sum + item.price * item.quantity + (item.sellerShippingFee || 0),
      0
    );
  const rawShippingCost =
    priceUpdateData?.updatedShippingCost ??
    selectedItemsList.reduce(
      (sum, item) => sum + (Number(item.shippingCost) || 0) * item.quantity,
      0
    );
  // Apply minimum shipping fee - if shipping is less than minimum but greater than 0, use minimum
  const selectedShippingCost =
    rawShippingCost > 0 && rawShippingCost < SHIPPING.MINIMUM_FEE
      ? SHIPPING.MINIMUM_FEE
      : rawShippingCost;

  // Calculate tax and service fee (use updated values if available, otherwise use API settings)
  const actualTax =
    priceUpdateData?.updatedTax ??
    (taxAndFeeSettings.length > 0
      ? calculateTax(selectedTotalAmount, taxAndFeeSettings)
      : 0);
  const serviceFee =
    priceUpdateData?.updatedFees ??
    (taxAndFeeSettings.length > 0
      ? calculateServiceFee(selectedTotalAmount, taxAndFeeSettings)
      : 0);
  const totalTaxAndFees = roundCurrency(actualTax + serviceFee);

  // Final total for payment (EXCLUDING shipping - paid on delivery)
  // Use updated total if available, otherwise calculate without shipping
  const baseSelectedFinalTotal =
    priceUpdateData?.updatedFinalTotal ??
    roundCurrency(selectedTotalAmount + totalTaxAndFees);

  // Apply promotion discount and payment method discount to final total
  const paymentMethodDiscountAmount =
    paymentMethodDiscount?.discountAmount ?? 0;
  const selectedFinalTotal = roundCurrency(
    Math.max(
      0,
      baseSelectedFinalTotal -
        appliedPromoCode.discountAmount -
        paymentMethodDiscountAmount
    )
  );

  // Grand total (for display purposes - includes shipping paid on delivery)
  const grandTotal = roundCurrency(selectedFinalTotal + selectedShippingCost);

  const isShippingBusy = isBuyNowFlow
    ? isBuyNowCalculatingShipping
    : isCalculatingShipping;

  const nonPaymentPrimaryActionLabel = useMemo(() => {
    switch (currentStepId) {
      case "summary":
        if (isShippingBusy) {
          return t("checkout.calculatingShippingWait");
        }
        if (hasSelectedItemsNeedingShipping) {
          return t("checkout.waitingForShipping");
        }
        return t("checkout.continueToDelivery");
      case "delivery":
        return t("checkout.continueToPayment");
      case "payment":
      default:
        return t("checkout.completePaymentBelow");
    }
  }, [currentStepId, isShippingBusy, hasSelectedItemsNeedingShipping, t]);

  const nonPaymentPrimaryActionDisabled =
    currentStepId === "payment" ||
    (currentStepId === "summary" &&
      (selectedItemsList.length === 0 ||
        isShippingBusy ||
        hasSelectedItemsNeedingShipping));
  const secondaryActionLabel = activeStep > 0 ? t("checkout.back") : undefined;

  const previousStepIdRef = useRef<StepId>(currentStepId);
  useEffect(() => {
    const previousStepId = previousStepIdRef.current;
    if (previousStepId === "payment" && currentStepId !== "payment") {
      setSnackbarMessage(null);
    }
    previousStepIdRef.current = currentStepId;
  }, [currentStepId]);

  useEffect(() => {
    if (currentStepId !== "payment" && paymentMethodDiscount) {
      setPaymentMethodDiscount(null);
    }
  }, [currentStepId, paymentMethodDiscount]);

  // Handle item selection
  const handleItemSelectionChange = (itemId: string, selected: boolean) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (selected) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  };

  // Initialize selected items on first load
  useEffect(() => {
    if (isBuyNowFlow) return;

    if (selectedItems.size === 0 && items.length > 0) {
      setSelectedItems(new Set(items.map((item) => item.cartItemId)));
    }
  }, [items, selectedItems.size, isBuyNowFlow]);

  const handlePriceUpdate = useCallback(
    (updateData: {
      updatedTotalPrice?: number;
      updatedShippingCost?: number;
      updatedTax?: number;
      updatedFees?: number;
      updatedFinalTotal?: number;
    }) => {
      setPriceUpdateData(updateData);
      // If buy-now flow, also update the buy-now item shipping cost if provided
      if (isBuyNowFlow && updateData.updatedShippingCost !== undefined) {
        setBuyNowShippingCost(updateData.updatedShippingCost);
      }
    },
    [isBuyNowFlow]
  );

  // Redirects / gates
  if (!isBuyNowFlow && items.length === 0) {
    return (
      <Box
        className="notranslate"
        translate="no"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
          backgroundColor: "#f5f5f5",
        }}
      >
        <Paper elevation={2} sx={{ p: 4, textAlign: "center", maxWidth: 640 }}>
          <ShoppingCartIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
          <Typography variant="h4" sx={{ mb: 2, color: "text.primary" }}>
            {t("cart.yourCartIsEmpty")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            {t("checkout.addItemsToCart")}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/")}
            sx={{
              backgroundColor: amazonColors.yellow,
              color: amazonColors.text.primary,
              "&:hover": { backgroundColor: amazonColors.yellowHover },
            }}
          >
            {t("cart.continueShopping")}
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box
        className="notranslate"
        translate="no"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
          backgroundColor: "#f5f5f5",
        }}
      >
        <Paper elevation={2} sx={{ p: 4, textAlign: "center", maxWidth: 640 }}>
          <WarningIcon sx={{ fontSize: 64, color: "warning.main", mb: 2 }} />
          <Typography variant="h4" sx={{ mb: 2, color: "text.primary" }}>
            {t("checkout.authenticationRequired")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            {t("checkout.pleaseLoginToComplete")}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/login")}
            sx={{
              backgroundColor: amazonColors.orange,
              color: "white",
              "&:hover": { backgroundColor: amazonColors.orangeHover },
            }}
          >
            {t("checkout.goToLogin")}
          </Button>
        </Paper>
      </Box>
    );
  }

  const handlePaymentSuccess = (result: PaymentSuccess) => {
    allowPaymentNavigationRef.current = true;
    setSnackbarMessage(null);

    trackPurchase({
      value:
        typeof result.amount === "number" && Number.isFinite(result.amount)
          ? result.amount
          : selectedFinalTotal,
      currency: result.currency?.toUpperCase() || "USD",
      numItems: selectedItemsCount,
      contentIds: selectedItemsList.map((item) => item.id),
      contents: selectedItemsList.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        item_price: item.price,
      })),
    });

    // Remove successfully checked out items from the cart (skip for buy-now flow)
    if (!isBuyNowFlow) {
      const selectedProductIds = Array.from(selectedItems);
      removeSelectedItems(selectedProductIds);
    }

    const fallbackName = [user?.firstName, user?.lastName]
      .filter((part) => Boolean(part && part.trim()))
      .join(" ")
      .trim();
    const pickupContactName =
      (deliveryInfo.fullName && deliveryInfo.fullName.trim()) ||
      fallbackName ||
      undefined;

    navigate("/order-confirmation", {
      state: {
        orderNumber: result.orderNumber,
        paymentIntentId: result.paymentIntentId,
        amount: result.amount,
        currency: result.currency,
        itemsTotal: selectedTotalAmount,
        pickupLocation: deliveryInfo.pickupLocation,
        shippingDue: selectedShippingCost,
        // Only pass a name if we actually have one; otherwise let the confirmation
        // page fall back to the profile name to avoid showing generic placeholders.
        customerName: pickupContactName || undefined,
      },
    });
  };

  const handlePaymentError = (error: PaymentError) => {
    setSnackbarMessage(error.message || t("checkout.paymentFailed"));
  };

  const handleNext = () => {
    if (currentStepId === "payment") {
      return;
    }

    setSnackbarMessage(null);

    if (currentStepId === "summary") {
      if (selectedItemsList.length === 0) {
        setSnackbarMessage(t("checkout.selectAtLeastOneItem"));
        return;
      }

      // Block progression if items need shipping calculation
      if (hasSelectedItemsNeedingShipping) {
        setSnackbarMessage(t("checkout.waitForShipping"));
        calculateMissingShipping();
        return;
      }

      // Also check if shipping is currently being calculated
      if (isShippingBusy) {
        setSnackbarMessage(t("checkout.calculatingShippingWait"));
        return;
      }

      const nextIndex = Math.min(activeStep + 1, stepsConfig.length - 1);
      goToStep(nextIndex);
      return;
    }

    if (currentStepId === "delivery") {
      if (
        !deliveryInfo.addressId ||
        !deliveryInfo.fullName ||
        !deliveryInfo.email ||
        !deliveryInfo.address ||
        !deliveryInfo.city ||
        !deliveryInfo.phone
      ) {
        setSnackbarMessage(t("checkout.selectAddressAndFillInfo"));
        return;
      }
      const nextIndex = Math.min(activeStep + 1, stepsConfig.length - 1);
      goToStep(nextIndex);
    }
  };

  const handleBack = () => {
    if (activeStep === 0 || isPaymentProcessing) return;
    goToStep(activeStep - 1);
  };

  const isPaymentStep = currentStepId === "payment";
  const sidebarPrimaryActionLabel = isPaymentStep
    ? paymentCtaState.label
    : nonPaymentPrimaryActionLabel;
  const sidebarPrimaryActionKey = isPaymentStep
    ? `payment-${paymentCtaState.key}`
    : `step-${currentStepId}`;
  const sidebarPrimaryActionDisabled = isPaymentStep
    ? paymentCtaState.disabled || !paymentCtaState.onClick
    : nonPaymentPrimaryActionDisabled;
  const sidebarPrimaryActionHandler =
    isPaymentStep && paymentCtaState.onClick
      ? paymentCtaState.onClick
      : handleNext;

  const sidebarProps = {
    itemsCount: selectedItemsCount,
    totalAmount: selectedTotalAmount,
    shippingCost: selectedShippingCost,
    tax: totalTaxAndFees,
    taxAmount: actualTax,
    serviceFeeAmount: serviceFee,
    finalTotal: grandTotal,
    taxAndFeeSettings,
    primaryActionLabel: sidebarPrimaryActionLabel,
    primaryActionKey: sidebarPrimaryActionKey,
    primaryActionDisabled: sidebarPrimaryActionDisabled,
    isCalculatingShipping: isShippingBusy,
    onPrimaryAction: sidebarPrimaryActionHandler,
    secondaryActionLabel,
    onSecondaryAction: activeStep > 0 ? handleBack : undefined,
    snackbarMessage: snackbarMessage || undefined,
    onSnackbarClose: () => setSnackbarMessage(null),
    appliedPromoCode,
    onPromoCodeChange: setAppliedPromoCode,
    paymentMethodDiscount,
  };

  const sidebarActionProps = {
    primaryActionLabel: sidebarPrimaryActionLabel,
    primaryActionKey: sidebarPrimaryActionKey,
    primaryActionDisabled: sidebarPrimaryActionDisabled,
    isCalculatingShipping: isShippingBusy,
    onPrimaryAction: sidebarPrimaryActionHandler,
    secondaryActionLabel,
    onSecondaryAction: activeStep > 0 ? handleBack : undefined,
    snackbarMessage: snackbarMessage || undefined,
    onSnackbarClose: () => setSnackbarMessage(null),
  };

  return (
    <Box
      className="notranslate"
      translate="no"
      sx={{
        display: "flex",
        justifyContent: "center",
        px: { xs: 1.5, sm: 2 },
        py: { xs: 2, md: 3 },
        backgroundColor: "#f5f5f5",
      }}
    >
      <Container
        maxWidth="lg"
        disableGutters
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header: Title + Steps */}
        <Box sx={{ pt: { xs: 1, md: 1.5 }, pb: { xs: 1.5, md: 2 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 1.25, md: 3 },
            }}
            className="notranslate"
            translate="no"
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: amazonColors.text.primary,
              }}
            >
              {t("checkout.title")}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {stepsConfig.map((step, index) => {
                const isActive = index === activeStep;
                const isComplete = index < activeStep;
                const labelColor =
                  isActive || isComplete
                    ? amazonColors.text.primary
                    : amazonColors.text.secondary;

                return (
                  <React.Fragment key={step.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: isComplete ? "#1faa64" : "#fff",
                          border: isActive
                            ? `2px solid ${amazonColors.orange}`
                            : isComplete
                              ? "none"
                              : `1px solid ${amazonColors.gray[300]}`,
                          color: isComplete ? "#fff" : amazonColors.orange,
                          flexShrink: 0,
                        }}
                      >
                        {isComplete ? (
                          <Check sx={{ fontSize: 12 }} />
                        ) : (
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: isActive
                                ? amazonColors.orange
                                : amazonColors.gray[400],
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isActive ? 700 : 500,
                          color: labelColor,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {step.label}
                      </Typography>
                    </Box>
                    {index < stepsConfig.length - 1 && (
                      <ChevronRight
                        sx={{ fontSize: 18, color: amazonColors.gray[400] }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        </Box>

        {isMobile && currentStepId === "summary" && (
          <>
            <Box
              sx={{
                mb: 1.5,
              }}
            >
              <CheckoutSidebar {...sidebarProps} showActions />
            </Box>
          </>
        )}

        {/* Body */}
        <Box
          sx={{
            display: "flex",
            gap: isMobile ? 2 : 3,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          {/* LEFT */}
          <Box
            sx={{
              flex: 1,
              pr: isMobile ? 0 : 1,
            }}
          >
            <Box sx={{ pb: 3 }}>
              {currentStepId === "summary" && (
                <OrderSummary
                  items={checkoutItems}
                  totalAmount={selectedTotalAmount}
                  shippingCost={selectedShippingCost}
                  tax={totalTaxAndFees}
                  finalTotal={selectedFinalTotal}
                  isCalculatingShipping={isShippingBusy}
                  hasItemsNeedingShipping={
                    isBuyNowFlow ? false : hasItemsNeedingShipping()
                  }
                  onItemSelectionChange={handleItemSelectionChange}
                  showSelection={!isBuyNowFlow}
                  paymentMethodDiscount={paymentMethodDiscount}
                />
              )}
              {currentStepId === "delivery" && (
                <DeliveryInformation
                  deliveryInfo={deliveryInfo}
                  isEditing={false}
                  onDeliveryInfoChange={handleDeliveryInfoChange}
                  onToggleEdit={() => undefined}
                  mobileSidebar={
                    isMobile ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        <CheckoutSidebarActions
                          {...sidebarActionProps}
                          topSpacing={0}
                        />
                        <CheckoutSidebar {...sidebarProps} showActions={false} />
                      </Box>
                    ) : undefined
                  }
                />
              )}
              {currentStepId === "payment" && (
                <PaymentSection
                  finalTotal={selectedFinalTotal}
                  customerEmail={deliveryInfo.email}
                  customerName={deliveryInfo.fullName}
                  items={selectedItemsList}
                  deliveryAddressId={deliveryInfo.addressId || ""}
                  deliveryInstructions={deliveryInfo.deliveryInstructions}
                  pickupLocation={deliveryInfo.pickupLocation}
                  totalPrice={selectedTotalAmount}
                  shippingCost={selectedShippingCost}
                  tax={actualTax}
                  fees={serviceFee}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  onOrderNumberReceived={setOrderNumber}
                  onCtaStateChange={setPaymentCtaState}
                  onPriceUpdate={handlePriceUpdate}
                  promotionCode={appliedPromoCode.code}
                  promotionCodeId={appliedPromoCode.codeId}
                  promotionDiscountAmount={appliedPromoCode.discountAmount}
                  onPaymentMethodDiscountChange={setPaymentMethodDiscount}
                  mobileSidebar={
                    isMobile ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        <CheckoutSidebarActions
                          {...sidebarActionProps}
                          topSpacing={0}
                        />
                        <CheckoutSidebar {...sidebarProps} showActions={false} />
                      </Box>
                    ) : undefined
                  }
                />
              )}
            </Box>

            {isMobile && currentStepId === "summary" && (
              <Box sx={{ mt: 2 }}>
                <CheckoutSidebarActions {...sidebarActionProps} />
              </Box>
            )}
          </Box>

          {!isMobile && (
            <Box sx={{ width: 380, flexShrink: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <CheckoutSidebar {...sidebarProps} />
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default CheckoutPage;
